
import { useState, useMemo } from 'react';
import { Tag } from '../../../../types';
import { useTags } from './useTags';
import { notify } from '../../../../utils/toast';

export const useTagsPage = () => {
    const {
        tags,
        loading,
        createTag,
        updateTag,
        deleteTag,
        deleteBulk,
        selectedIds,
        toggleSelection,
        toggleAllSelection,
        clearSelection,
        refetch
    } = useTags();

    const [searchTerm, setSearchTerm] = useState('');
    const [isFormModalOpen, setIsFormModalOpen] = useState(false);
    const [tagToEdit, setTagToEdit] = useState<Tag | undefined>(undefined);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [tagToDelete, setTagToDelete] = useState<Tag | undefined>(undefined);
    const [isBulkDeleteModalOpen, setIsBulkDeleteModalOpen] = useState(false);

    const filteredTags = useMemo(() => {
        return tags.filter(tag =>
            tag.name.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [tags, searchTerm]);

    const handleOpenCreateModal = () => {
        setTagToEdit(undefined);
        setIsFormModalOpen(true);
    };

    const handleOpenEditModal = (tag: Tag) => {
        setTagToEdit(tag);
        setIsFormModalOpen(true);
    };

    const handleOpenDeleteModal = (tag: Tag) => {
        setTagToDelete(tag);
        setIsDeleteModalOpen(true);
    };

    const handleSaveTag = async (data: Omit<Tag, 'id' | 'createdAt'>) => {
        try {
            if (tagToEdit) {
                await updateTag(tagToEdit.id, data);
                notify.success('Etiqueta atualizada com sucesso!');
            } else {
                await createTag(data);
                notify.success('Etiqueta criada com sucesso!');
            }
            setIsFormModalOpen(false);
        } catch (error) {
            notify.error('Erro ao salvar etiqueta');
        }
    };

    const handleConfirmDelete = async () => {
        if (tagToDelete) {
            try {
                await deleteTag(tagToDelete.id);
                notify.success('Etiqueta excluída com sucesso!');
                setIsDeleteModalOpen(false);
                setTagToDelete(undefined);
            } catch (error) {
                notify.error('Erro ao excluir etiqueta');
            }
        }
    };

    const handleBulkDelete = async () => {
        const ids = Array.from(selectedIds);
        try {
            await deleteBulk(ids);
            notify.success(`${ids.length} etiqueta(s) excluída(s) com sucesso!`);
            setIsBulkDeleteModalOpen(false);
        } catch (error) {
            notify.error('Erro ao excluir etiquetas');
        }
    };

    const handleDuplicate = async (tag: Tag) => {
        try {
            const { id, createdAt, ...data } = tag;
            await createTag({ ...data, name: `${data.name} (Cópia)` });
            notify.success('Etiqueta duplicada com sucesso!');
        } catch (error) {
            notify.error('Erro ao duplicar etiqueta');
        }
    };

    const handleRefresh = async () => {
        if (refetch) {
            await refetch();
            notify.success('Dados atualizados!');
        }
    };

    return {
        // Data & State
        tags,
        filteredTags,
        loading,
        selectedIds,
        searchTerm,
        isFormModalOpen,
        tagToEdit,
        isDeleteModalOpen,
        tagToDelete,
        isBulkDeleteModalOpen,

        // Setters
        setSearchTerm,
        setIsFormModalOpen,
        setIsDeleteModalOpen,
        setIsBulkDeleteModalOpen,

        // Handlers
        toggleSelection,
        toggleAllSelection,
        clearSelection,
        handleOpenCreateModal,
        handleOpenEditModal,
        handleOpenDeleteModal,
        handleSaveTag,
        handleConfirmDelete,
        handleBulkDelete,
        handleDuplicate,
        handleRefresh
    };
};
